import React from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Link,
  Divider,
} from "@mui/material";

const TermsOfService: React.FC = () => {
  const brandColor = "#371b17";

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#faf8f6", py: 4 }}>
      <Container maxWidth="md">
        {/* Main Content Card */}
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 3,
            bgcolor: "#fff",
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{
              color: brandColor,
              fontWeight: 700,
              mb: 2,
              textAlign: { xs: "center", md: "left" },
            }}
          >
            GoTaste Subscription Terms of Service
          </Typography>

          <Typography variant="subtitle2" color="text.secondary" paragraph>
            Last Updated: May 2026
          </Typography>

          <Divider sx={{ my: 3, borderColor: brandColor }} />

          <Box sx={{ typography: "body1", lineHeight: 1.8, color: "#333" }}>
            <Typography paragraph>
              These subscription terms apply to GoTaste Basic Collection and
              Delivery Premium subscriptions, which are one-off, non- recurring
              subscriptions.
            </Typography>

            <Typography paragraph>
              The terms and conditions will be subject to any rights you may
              have under consumer law to which we are bound and which cannot be
              waived by contract.
            </Typography>

            <Typography paragraph>
              By agreeing to pay for a GoTaste subscription, you agree to these
              terms and conditions.
            </Typography>

            <Typography paragraph>
              If you have any questions about your subscription, or need any
              help, please email us at{" "}
              <Link href="mailto:help@gotaste.uk" sx={{ color: brandColor }}>
                help@gotaste.uk
              </Link>
              .
            </Typography>

            <Typography variant="h6" sx={{ color: brandColor, mt: 3, mb: 1 }}>
              1. What do I receive with my GoTaste subscription?
            </Typography>
            <Typography paragraph>
              GoTaste offers a variety of subscriptions. The benefits included
              in your subscription package will be shown on our website during
              the sign-up process. By taking out a particular GoTaste
              subscription, you will receive all of the benefits described.
            </Typography>
            <Typography paragraph>
              We may change the benefits of your GoTaste subscription from time
              to time as set out below (Changes to your GoTaste subscription).
            </Typography>
            <Typography paragraph>
              You can start receiving your subscription benefits by signing in
              on our website using the details associated with your account,
              straight after sign-up. Further information can be found in your
              subscription section in the restaurant module after you sign into
              your account”.
            </Typography>
            <Typography paragraph>
              If you think there is something wrong with your subscription,
              please email us.
            </Typography>

            <Typography variant="h6" sx={{ color: brandColor, mt: 3, mb: 1 }}>
              2. How can I sign up to get a GoTaste subscription?
            </Typography>
            <Typography paragraph>
              You can sign up to get a subscription from your restaurant
              dashboard at gotasteadmin.uk.
            </Typography>
            <Typography paragraph>
              As part of the process, you will need to register for a GoTaste
              account if you don't already have one. You will then need to sign
              in, using your GoTaste account details, within the Guardian apps
              and on the website to access your subscription benefits.
            </Typography>
            <Typography paragraph>
              By submitting our online form, you are making an offer to sign up
              to a GoTaste subscription.
            </Typography>
            <Typography paragraph>
              If we accept your offer, we will send you confirmation by email
              and it will then constitute a binding contract. Nothing else that
              we do or say will amount to any acceptance of that offer.
            </Typography>
            <Typography paragraph>
              We will not be responsible for any failure by you to receive such
              confirmation, which results from your failure to supply us with
              correct contact details.
            </Typography>
            <Typography paragraph>
              If you sign up online, you are able to correct errors on your
              online form up to the point at which you submit your form. After
              this point, we will assume that all information provided is
              correct, unless and until you make any changes to your personal
              information, and will process your form accordingly.
            </Typography>
            <Typography paragraph>
              To the maximum extent permitted by applicable law, we reserve the
              right to refuse any offer of support and therefore to refuse to
              provide you with the subscription. We will do so for reasons such
              as where we suspect fraud, falso information being provided or
              where we have questions about the origin of your funds or
              information provided on your account.
            </Typography>

            <Typography variant="h6" sx={{ color: brandColor, mt: 3, mb: 1 }}>
              3. Advert-free listing
            </Typography>
            <Typography paragraph>
              No subscription packages include an advert-free listing currently.
            </Typography>
            <Typography paragraph>
              You may also see “Paid content/Paid for by” content. “Paid
              content/paid for by” is used to describe advertisement features
              that are paid for and controlled by the advertiser rather than
              GoTaste. This content is produced by the advertiser and the
              advertiser is solely responsible for this content. Each individual
              advertiser is solely responsible for the content of its
              advertising material.
            </Typography>
            <Typography paragraph>
              We accept no responsibility for third party advertising or “Paid
              content/Paid for by” content, including, without limitation, any
              error, omission or inaccuracy therein.
            </Typography>

            <Typography variant="h6" sx={{ color: brandColor, mt: 3, mb: 1 }}>
              4. Your subscription is personal
            </Typography>
            <Typography paragraph>
              Your Guardian subscription is personal to you and
              non-transferable.
            </Typography>
            <Typography paragraph>
              Our acceptable use policy is: where we suspect fraudulent activity
              or we believe that you are sharing your subscription account
              details unreasonably, excessively, or beyond the limits of your
              subscription package, we have the right to lock your subscription
              account(so you can’t share your details anymore) or cancel your
              subscription under section(Cancellation and Refunds).
            </Typography>

            <Typography variant="h6" sx={{ color: brandColor, mt: 3, mb: 1 }}>
              5. GoTaste subscription renewals
            </Typography>
            <Typography paragraph>
              You can take out your GoTastesubscription on a monthly basis only
              and this is a one-off non recurring payment. Your subscription
              will automatically cancel at the end of the period and will need
              to be manually renewed again.
            </Typography>
            <Typography paragraph>
              Automatic Renewal: Unless you cancel or we end your
              subscription(please see section 10 (Cancellation and Ref unds) for
              how to cancel your subscription), your subscription will not
              automatically renew on these terms on a rolling monthly or annual
              basis. If you do not cancel, we will not automatically charge your
              chosen payment account the applicable price for the renewed
              subscription. We will send you a reminder before your subscription
              is due to expire with details of how you can manually renew
              otherwise it will be automatically cancelled.
            </Typography>

            <Typography variant="h6" sx={{ color: brandColor, mt: 3, mb: 1 }}>
              6. Changes to your GoTaste subscription
            </Typography>
            <Typography paragraph>
              We reserve the right to change from time to time:
            </Typography>
            <Box component="ul" sx={{ pl: 4, mb: 2, color: "#333" }}>
              <li>
                the benefits that you receive as part of your subscription;
              </li>
              <li>the GoTaste and annual subscription price; and/or</li>
              <li>the Guardian apps (including their functionality); and/or</li>
              <li>
                the third party platforms on which we make our digital app
                available.
              </li>
            </Box>
            <Typography paragraph>
              We also reserve the right to withdraw the supply of your
              subscription and its benefits at any time.
            </Typography>
            <Typography paragraph>If we change:</Typography>
            <Box component="ul" sx={{ pl: 4, mb: 2, color: "#333" }}>
              <li>
                your subscription benefits, we will give you at least 30 days’
                notice in advance. This change will take effect at the end of
                the notice period; and/or
              </li>
              <li>
                the monthly and annual price for your subscription, we will give
                you at least 30 days’ notice in advance of this change. You will
                need to pay this new price to continue receiving the
                subscription and its benefits. Any change to the price will
                apply from your next billing cycle.
              </li>
            </Box>
            <Typography paragraph>
              If we change your subscription benefits and/or the monthly and
              annual price, we will give you this advance notice so that you can
              choose whether or not to cancel your subscription.
            </Typography>
            <Typography paragraph>
              If you are unhappy with the changes, you will need to cancel your
              subscription, before the changes take effect. If you don’t do so,
              you will only be able to cancel your GoTaste subscription in
              accordance with the section(Cancellation and Ref unds).
            </Typography>
            <Typography paragraph>
              You will continue to receive your subscription and its benefits,
              until you cancel your subscription payments.
            </Typography>
            <Typography paragraph>
              We can always change the GoTaste apps from time to time to
              implement technical adjustments, updates and/or improvements, for
              example to address security threats or remedy bugs. These are
              changes that do not affect your use of the GoTaste apps and so we
              will not give you prior notice and you are not able to cancel your
              GoTaste subscription due to these changes. You might be asked to
              reinstall the relevant GoTaste app, accept updates or log out and
              log back into your GoTaste account.
            </Typography>

            <Typography variant="h6" sx={{ color: brandColor, mt: 3, mb: 1 }}>
              7. Can I change to another GoTaste subscription package?
            </Typography>
            <Typography paragraph>
              Yes. You can change to another GoTaste subscription package by
              clicking on Upgrade in the restaurant module, next to the online
              restaurant’s name you wish to upgrade Changes will take effect
              immediately.
            </Typography>

            <Typography variant="h6" sx={{ color: brandColor, mt: 3, mb: 1 }}>
              8. Privacy
            </Typography>
            <Typography paragraph>
              You can read our Privacy Policy at{" "}
              <Link
                href="https://www.gotaste.uk/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: brandColor }}
              >
                https://www.gotaste.uk/privacy-policy
              </Link>{" "}
              for details regarding how we use your personal information.
            </Typography>

            <Typography variant="h6" sx={{ color: brandColor, mt: 3, mb: 1 }}>
              9. Payment of your subscription fees
            </Typography>
            <Typography paragraph>
              You agree to pay the subscription price for your GoTaste
              subscription. The price of your subscription and payment options
              will be shown during the checkout process and in your confirmation
              email. Subscription prices may vary from time to time and by
              country.
            </Typography>
            <Typography paragraph>
              You must provide us with complete and accurate payment information
              at the time you sign up and ensure that you keep your payment
              information up-to-date. We will not provide a refund if you cannot
              access your subscription benefits due to your payment details
              being incorrect.
            </Typography>
            <Typography paragraph>
              The date your first payment will be taken will be shown on the
              check-out before you submit your online form.
            </Typography>
            <Typography paragraph>
              Your subscription will continue unless and until it is ended by
              either of us in accordance with section(Cancellation and Ref
              unds).
            </Typography>
            <Typography paragraph>
              From time to time, we may introduce free trials or other
              promotional offers for our subscriptions. These offers will be
              subject to their own terms, which will be published together with
              the relevant offer, and subject to availability. We reserve the
              right to offer and withdraw free trial and promotional offers at
              any time and in no way guarantee that an offer will be available
              at any particular time. Promotions or discounts may not be
              available to subscribers who hold an existing subscription with
              GoTaste.
            </Typography>

            <Typography variant="h6" sx={{ color: brandColor, mt: 3, mb: 1 }}>
              10. Cancellation and Refunds
            </Typography>
            <Typography paragraph>
              <strong>
                (a) Your right to cancel your subscription during the first 48
                hours
              </strong>
              <br />
              You can cancel your subscription at any time during the first 48
              hours from sign-up(up to and including day 2), without giving any
              reason, provided you have not created any menu for any upgraded
              restaurant If your first payment has not been taken you will not
              be charged. If your first payment has been taken you will receive
              a full refund and will not be billed further. Cancellation of your
              subscription will take effect immediately and you will no longer
              receive your subscription benefits.
            </Typography>
            <Typography paragraph>
              We will pay your refund without undue delay, and not later than 30
              days after the day on which we are informed about your decision to
              cancel your subscription.
            </Typography>
            <Typography paragraph>
              We will pay your refund using the same means of payment as you
              used for the initial payment or through bank transfer to an
              account in your name at our discretion.
            </Typography>
            <Typography paragraph>
              If you do not cancel before your next payment date, your
              subscription will not automatically renew for another month and
              you will not be charged the applicable subscription price for the
              renewal. You will then need to cancel before the next payment
              date.
            </Typography>
            <Typography paragraph>
              <strong>(c) Your other rights to cancel your subscription</strong>
              <br />
              If you are not happy with any changes we make, under section
              6(Changes to your Guardian subscription), to: ● your subscription
              benefits; and/or ● the subscription monthly and annual price.
            </Typography>
            <Typography paragraph>
              you should cancel your subscription as soon as possible before the
              changes take effect.
            </Typography>
            <Typography paragraph>
              If you are not happy with any material changes we make to these
              terms and conditions under section 14(Changes to these terms and
              conditions), you should cancel your subscription as soon as
              possible before the changes take effect.
            </Typography>
            <Typography paragraph>
              If you wish to cancel your subscription, you can cancel by(1)
              letting your subscription expire”; or(2) contacting our customer
              services team using the contact details help@gotaste.uk
            </Typography>
            <Typography paragraph>
              Cancellations should be made by you following the process
              described in this clause. If you use a third party(such as a
              subscription management company) to cancel or amend your
              subscription on your behalf we may not be able to process your
              request.
            </Typography>
            <Typography paragraph>
              <strong>(e) Our right to cancel your subscription</strong>
              <br />
              We may cancel your subscription immediately if you breach any of
              your material obligations under these terms and conditions,
              including if we do not receive a payment when due from you or if
              you breach our acceptable use rules in section 4 or provide false
              or fraudulent information. We will make reasonable efforts to
              contact you before your subscription is cancelled in this way. If
              we cancel your subscription for this reason, you will not receive
              a refund of any amounts paid.
            </Typography>
            <Typography paragraph>
              We may also cancel your subscription if we are unable to provide
              you with the subscription, for reasons such as incorrect
              information provided by you, technical reasons, or reasons outside
              of our control. We will make reasonable efforts to contact you
              before your GoTaste subscription is cancelled in this way. Under
              these circumstances, you may be entitled to a refund in accordance
              with section 10(f) below.
            </Typography>
            <Typography paragraph>
              We may stop providing you with your GoTaste subscription at any
              time by providing you with at least 30 days’ prior notice.
            </Typography>
            <Typography paragraph>
              Finally, we may immediately stop providing you with your GoTaste
              subscription at any time if there are exceptional circumstances or
              circumstances beyond our reasonable control(such as the acts of
              Apple, Google or other third party app stores).
            </Typography>
            <Typography paragraph>
              <strong>(f) Refunds</strong>
              <br />
              We offer refunds in the circumstances described in sections 10(a),
              10(c) and 10(e).
            </Typography>
            <Typography paragraph>
              We also offer refunds if: 1. we are unable to provide you with the
              subscription through no fault of yours; If either of the two
              circumstances(i) or(ii) above apply, you may cancel your
              subscription without giving you a proportionate refund for the
              unused part of your subscription. However you may also have refund
              rights arising under applicable consumer protection laws.
            </Typography>
            <Typography paragraph>
              If you have a query about obtaining a refund, please contact our
              customer services team using the contact details in section
              13(Customer Services).
            </Typography>

            <Typography variant="h6" sx={{ color: brandColor, mt: 3, mb: 1 }}>
              11. Suspension of GoTaste apps or your GoTaste subscription
            </Typography>
            <Typography paragraph>
              We may need to suspend the supply of any of our GoTasteapps or
              your GoTastesubscription at any particular time: ● to deal with
              technical, legal, regulatory or security issues; ● due to
              circumstances beyond our reasonable control; ● if we reasonably
              suspect fraud; and/or ● if our third party app store providers ask
              or require us to suspend the supply.
            </Typography>
            <Typography paragraph>
              We will try to let you know in advance if we are suspending supply
              but this may not always be possible, for example if we need to
              suspend urgently or due to an emergency. Where we suspend supply,
              we will work hard to try and minimise the period of
              unavailability.
            </Typography>
            <Typography paragraph>
              To the extent permitted by law, we will not be liable to you for
              any loss due to your loss of access if we suspend the supply of
              any GoTaste app and your Guardian subscription.
            </Typography>
            <Typography paragraph>
              Our third party app store providers may also separately suspend
              any of our GoTaste apps, and this will be beyond our control. We
              will not be liable to you for your loss of access if this happens.
            </Typography>
            <Typography paragraph>
              If your subscription includes access to any third party app, that
              third party is entirely responsible for the delivery of their app
              to you. GoTastehas no control or responsibility for third party
              provided apps. We will not be liable to you for any unavailability
              of third party provided apps.
            </Typography>

            <Typography variant="h6" sx={{ color: brandColor, mt: 3, mb: 1 }}>
              12. Our Responsibility To You
            </Typography>
            <Typography paragraph>
              We are legally obliged to supply you with your GoTaste
              subscription in a manner which conforms with these terms and
              conditions.
            </Typography>
            <Typography paragraph>
              If we are unable to perform our obligations due to circumstances
              beyond our control, section 11 will apply and you will not be
              entitled to a refund.
            </Typography>
            <Typography paragraph>
              We shall not be responsible for any failure to provide you with
              the subscription if this is due to the supply of incorrect contact
              or payment details or other necessary information by you or any
              other act committed by you, and you are not entitled to a refund
              if this is the case.
            </Typography>
            <Typography paragraph>
              Please note that issues with your mobile network operator and/or
              data connection with your device may mean that you are unable to
              access the subscription at any particular time – this is outside
              of our control and we shall not be responsible in these
              circumstances.
            </Typography>
            <Typography paragraph>
              We exclude our liability to you to the maximum extent permitted by
              law(for example, nothing in these terms shall exclude or limit our
              liability for death or personal injury resulting from our
              negligence).
            </Typography>
            <Typography paragraph>
              In all other events, our total liability to you however arising in
              connection with your subscription will not exceed(i) one(1) times
              the monthly amount you pay. In certain jurisdictions, it is not
              legally permissible for us to limit our liability in this way. We
              do not limit our liability to you where it is not legally
              permissible for us to do so. Our exclusions of liability shall not
              apply to any damages arising from death or personal injury caused
              by our negligence or that of any of our employees or agents or any
              other liability which we cannot limit by law.
            </Typography>
            <Typography paragraph>
              You may have statutory consumer rights(including refund rights in
              certain countries) that apply to your subscription which cannot be
              limited, excluded or modified. Nothing in these terms and
              conditions limits, excludes or modifies any such consumer rights.
            </Typography>
            <Typography paragraph>
              If you are in the United States, section 18 also applies to you.
            </Typography>

            <Typography variant="h6" sx={{ color: brandColor, mt: 3, mb: 1 }}>
              13. Customer Services/ Any other questions
            </Typography>
            <Typography paragraph>
              If you have a query or complaint about your subscription, please
              contact us using any of the contact details below: Email:{" "}
              <Link href="mailto:help@gotaste.uk" sx={{ color: brandColor }}>
                help@gotaste.uk
              </Link>
            </Typography>

            <Typography variant="h6" sx={{ color: brandColor, mt: 3, mb: 1 }}>
              14. Changes to these terms and conditions
            </Typography>
            <Typography paragraph>
              We may update these terms and conditions from time to time by
              providing you with at least 30 days’ prior notice of any material
              changes. The changes will apply to your subscription at the end of
              the 30 days.
            </Typography>
            <Typography paragraph>
              If you do not wish to accept the new terms and conditions you
              should not continue with your subscription as this will indicate
              your agreement to the new terms and conditions. If you do not wish
              to accept the new terms and conditions, you should cancel your
              subscription within the 30 days’ notice period.
            </Typography>

            <Typography variant="h6" sx={{ color: brandColor, mt: 3, mb: 1 }}>
              15. Corporate Information
            </Typography>
            <Typography paragraph>
              Your GoTaste subscription is provided by GO2TASTE LimitedMin the
              UK.
            </Typography>

            <Typography variant="h6" sx={{ color: brandColor, mt: 3, mb: 1 }}>
              16. General
            </Typography>
            <Typography paragraph>
              We intend to rely on the written terms set out in these terms and
              conditions.
            </Typography>
            <Typography paragraph>
              If a court invalidates some of these terms and conditions, or some
              of these terms and conditions become illegal or unlawful, the rest
              of these terms and conditions will continue to apply.
            </Typography>
            <Typography paragraph>
              We can transfer our rights and obligations under these terms and
              conditions to another person at any time and we will give you
              reasonable prior notice if we plan to do this, including
              information about any right to cancel or refund rights that might
              apply. You cannot transfer these terms and conditions to anyone
              else.
            </Typography>
            <Typography paragraph>
              Nobody else has any rights under these terms and conditions – they
              are between you and us only.
            </Typography>
            <Typography paragraph>
              Even if we delay in enforcing these terms and conditions, we can
              still enforce them later.
            </Typography>
            <Typography paragraph>
              These terms and conditions are governed by English law. All claims
              in respect of these terms and conditions are subject to the
              exclusive jurisdiction of the English courts, except if you live
              in Wales, Scotland, Northern Ireland or the EU, you can choose to
              bring a claim in the country where you live and you remain
              entitled to any more favourable mandatory provisions of the local
              law.
            </Typography>

            <Typography variant="h6" sx={{ color: brandColor, mt: 3, mb: 1 }}>
              17. Additional terms and conditions for subscribers in Australia
            </Typography>
            <Typography paragraph>
              Notwithstanding the above, for users in Australia, these terms and
              conditions are governed by the laws of New South Wales and are
              subject to the exclusive jurisdiction of the courts of New South
              Wales.
            </Typography>

            <Typography variant="h6" sx={{ color: brandColor, mt: 3, mb: 1 }}>
              18. Additional terms and conditions for subscribers in the United
              States
            </Typography>
            <Typography
              paragraph
              sx={{
                fontSize: "0.9rem",
                bgcolor: "#f5f5f5",
                p: 2,
                borderRadius: 1,
                whiteSpace: "pre-wrap", // Preserves formatting for legal text
              }}
            >
              IN ADDITION TO THE OTHER LIMITATIONS AND EXCLUSIONS IN THESE TERMS
              AND CONDITIONS, FOR USERS IN THE UNITED STATES OF AMERICA ONLY,
              THE PARTIES(WE AND YOU) WAIVE THE RIGHT TO A JURY TRIAL AND TO
              PARTICIPATE IN A CLASS ACTION, OR ANY OTHER FORM OF REPRESENTATIVE
              ACTION, WHETHER IN COURT OR IN ARBITRATION. EACH PARTY MAY PROCEED
              IN ANY DISPUTE ONLY IN THAT PARTY’S INDIVIDUAL CAPACITY, AND NOT
              AS A PLAINTIFF OR CLASS MEMBER IN ANY CLASS ACTION OR CLASS
              ARBITRATION.
              {"\n\n"}
              IN ADDITION TO THE OTHER LIMITATIONS AND EXCLUSIONS IN THESE TERMS
              AND CONDITIONS, FOR USERS IN THE UNITED STATES OF AMERICA ONLY,
              THE GUARDIAN SUBSCRIPTION IS PROVIDED TO YOU “AS IS” AND WITH ALL
              FAULTS AND DEFECTS WITHOUT WARRANTY OF ANY KIND. TO THE MAXIMUM
              EXTENT PERMITTED UNDER APPLICABLE LAW, WE, ON OUR OWN BEHALF AND
              ON BEHALF OF OUR AFFILIATES AND OUR AND THEIR RESPECTIVE LICENSORS
              AND SERVICE PROVIDERS, EXPRESSLY DISCLAIM ALL WARRANTIES, WHETHER
              EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE, WITH RESPECT TO THE
              GUARDIAN APPS AND THE GUARDIAN SUBSCRIPTION, INCLUDING ALL IMPLIED
              WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
              TITLE, AND NON-INFRINGEMENT, AND WARRANTIES THAT MAY ARISE OUT OF
              COURSE OF DEALING, COURSE OF PERFORMANCE, USAGE, OR TRADE
              PRACTICE. WITHOUT LIMITATION TO THE FOREGOING, WE PROVIDE NO
              WARRANTY OR UNDERTAKING, AND MAKE NO REPRESENTATION OF ANY KIND
              THAT THE GUARDIAN APPS OR GUARDIAN SUBSCRIPTION WILL MEET YOUR
              REQUIREMENTS, ACHIEVE ANY INTENDED RESULTS, BE COMPATIBLE, OR WORK
              WITH ANY OTHER SOFTWARE, APPLICATIONS, SYSTEMS, OR SERVICES,
              OPERATE WITHOUT INTERRUPTION, MEET ANY PERFORMANCE OR RELIABILITY
              STANDARDS, OR BE ERROR-FREE, OR THAT ANY ERRORS OR DEFECTS CAN OR
              WILL BE CORRECTED.
              {"\n\n"}
              IN ADDITION TO THE OTHER LIMITATIONS AND EXCLUSIONS IN THESE TERMS
              AND CONDITIONS, FOR USERS IN THE UNITED STATES OF AMERICA ONLY, WE
              EXCLUDE OUR LIABILITY TO YOU TO THE MAXIMUM EXTENT PERMITTED BY
              LAW.
              {"\n\n"}
              IN NO EVENT SHALL WE BE LIABLE TO YOU OR ANY THIRD PARTY FOR ANY
              LOST PROFITS OR INDIRECT, CONSEQUENTIAL, INCIDENTAL, EXEMPLARY,
              PUNITIVE, STATUTORY OR SPECIAL DAMAGES ARISING OUT OF OR RELATED
              TO THIS AGREEMENT, UNDER ANY THEORY OF LIABILITY, WHETHER IN
              CONTRACT, TORT (INCLUDING, WITHOUT LIMITATION, NEGLIGENCE),
              INDEMNITY, BREACH OF WARRANTY OR OTHER THEORY AND REGARDLESS OF
              WHETHER WE WERE AWARE OF THE POSSIBILITY OF SUCH DAMAGES.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default TermsOfService;
